import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    TextField,
    InputAdornment,
    Stack,
    Card,
    Avatar,
    IconButton,
    Radio,
    Skeleton,
    Chip
} from '@mui/material';
import {
    Search as SearchIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Close as CloseIcon,
    Phone as PhoneIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { EMPLOYEE_STORE } from '../../config/constants';

export default function SelectSingleEmployeeModal({
    open,
    onClose,
    onSelect,
    selectedEmployee = null,
    title = "Select Employee"
}) {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) return;
        loadEmployees();
    }, [open]);

    useEffect(() => {
        // Initialize selected from prop
        setSelected(selectedEmployee);
    }, [selectedEmployee]);

    async function loadEmployees() {
        setLoading(true);
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const allEmployees = await store.getAll();
            
            // Map employees - include ALL fields
            const mappedEmployees = allEmployees.map(e => ({
                ...e,
                FullName: e.FullName || e.fullName || 'Unknown',
                Division: e.Division || e.division || 'No Division',
                JobClassification: e.JobClassification || e.jobClassification || '',
                Phone: e.Phone || e.phone || '',
                email: e.email || ''
            }));
            
            // Filter out inactive employees
            const activeEmployees = mappedEmployees.filter(e => 
                e.status !== false && e.status !== 0
            );
            
            setEmployees(activeEmployees);
        } catch (error) {
            console.error('Error loading employees:', error);
        } finally {
            setLoading(false);
        }
    }

    const filteredEmployees = useMemo(() => {
        if (!searchTerm) return employees;
        
        const search = searchTerm.toLowerCase();
        return employees.filter(emp => {
            const name = (emp.FullName || '').toLowerCase();
            const division = (emp.Division || '').toLowerCase();
            const job = (emp.JobClassification || '').toLowerCase();
            const phone = (emp.Phone || '').toLowerCase();
            const email = (emp.email || '').toLowerCase();
            
            return name.includes(search) || 
                   division.includes(search) || 
                   job.includes(search) ||
                   phone.includes(search) ||
                   email.includes(search);
        });
    }, [employees, searchTerm]);

    function handleSelect(employee) {
        setSelected(employee);
        onSelect(employee);
        handleClose();
    }

    function handleClose() {
        setSearchTerm('');
        onClose();
    }

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { height: '80vh', maxHeight: 700 }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{title}</Typography>
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 2 }}>
                {/* Search Bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, division, position, phone or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Employee count */}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} found
                    </Typography>
                </Box>

                {/* Employee List */}
                <Box sx={{ height: 'calc(100% - 100px)', overflow: 'auto' }}>
                    {loading ? (
                        <Stack spacing={1}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <Skeleton key={i} variant="rectangular" height={80} />
                            ))}
                        </Stack>
                    ) : filteredEmployees.length === 0 ? (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                No employees found
                            </Typography>
                        </Card>
                    ) : (
                        <Stack spacing={1}>
                            {filteredEmployees.map((employee, idx) => {
                                const isSelected = selected?.id === employee.id || 
                                                 (selected?.FullName === employee.FullName);
                                
                                return (
                                    <Card
                                        key={employee.id || idx}
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                            border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5',
                                                transform: 'translateY(-2px)',
                                                boxShadow: 2
                                            }
                                        }}
                                        onClick={() => handleSelect(employee)}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Radio
                                                checked={isSelected}
                                                sx={{ p: 0 }}
                                            />
                                            <Avatar sx={{ 
                                                backgroundColor: '#2196f3',
                                                width: 40,
                                                height: 40
                                            }}>
                                                <PersonIcon fontSize="small" />
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="500">
                                                    {employee.FullName}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                    {employee.JobClassification && (
                                                        <Chip
                                                            label={employee.JobClassification}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: '0.75rem' }}
                                                        />
                                                    )}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <BusinessIcon sx={{ fontSize: 14, color: '#666' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Division {employee.Division}
                                                        </Typography>
                                                    </Box>
                                                    {employee.Phone && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <PhoneIcon sx={{ fontSize: 14, color: '#666' }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {employee.Phone}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {employee.email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <EmailIcon sx={{ fontSize: 14, color: '#666' }} />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {employee.email}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}